import type { TEST_CONFIG as OWNER_WAVE_CONFIG } from 'js/ab_tests/tests/autotest_logged_in_profile_owner_2026_q3';
import type { TEST_CONFIG as OWNER_SMOKE_CONFIG } from 'js/ab_tests/tests/autotest_logged_in_profile_owner_smoke';
import type { TEST_CONFIG as VISITOR_WAVE_CONFIG } from 'js/ab_tests/tests/autotest_logged_in_profile_visitor_2026_q3_v2';

import type { WorkFacts } from './pageData';
import type { ProfileCard, SlotName, SurfacePart } from './surface';

// Buckets derive from the generated test modules so the registry can never
// drift from the ymls: an arm name that isn't in a yml fails to typecheck.
export type VisitorBucket = (typeof VISITOR_WAVE_CONFIG.alternatives)[number];
export type OwnerBucket = (typeof OWNER_WAVE_CONFIG.alternatives)[number];
export type Bucket = VisitorBucket | OwnerBucket;
export type Arm = Exclude<Bucket, 'control'>;

// The owner smoke yml carries the same arm names, so a smoke bucket is typed
// but never dispatched — the harness gate keeps it rendering control. The
// visitor wave-2 has no smoke.
export type SmokeBucket = (typeof OWNER_SMOKE_CONFIG.alternatives)[number];
export type AnyBucket = Bucket | SmokeBucket;

export type Mode = 'visitor' | 'owner';

// Every seam an arm gets. An arm is a deletable leaf that composes these and
// never reaches into the page — no page class, page global, fetch or beacon of
// its own. Nothing machine-checks that; each seam below owns the reach instead.
export type ArmContext = {
  viewedUserId: number;
  // The arm's own CSS selector — [data-liautotest-arm="<bucket>"], matching the
  // page shell the harness stamped — and the first argument to every recipe in
  // surface.ts. An arm never spells that attribute itself; this is the only
  // spelling of it an arm may hold.
  scope: string;
  // Mounts (once) an empty container the arm owns, at a named point on the
  // page, taking no layout until the arm fills it. Both anchors are
  // unconditional on this surface, so null means this is not the profile page —
  // it is NOT how you detect an empty corpus. A profile with no works still
  // renders the list container, so test that with cards().length.
  mountSlot: (name: SlotName) => HTMLElement | null;
  cards: () => ProfileCard[];
  reorderCards: (
    compare: (a: ProfileCard, b: ProfileCard) => number,
    scope?: 'section' | 'list',
  ) => void;
  // A SECOND data-liautotest-<name>, for an arm that wants a box flagged beyond
  // its own scope — on the page shell by default, elsewhere via target. Returns
  // the selector that now matches it, which is the only spelling of it an arm
  // may hold, or null when that box is not on this page.
  setSurfaceFlag: (
    name: string,
    options?: { value?: string; target?: SurfacePart },
  ) => string | null;
  // The arm's own stylesheet, injected once under an id keyed to the arm. Arms
  // never touch a shared stylesheet, which is what keeps them one file each.
  injectStyles: (css: string) => void;
  // Lazily fetched and memoized; an arm that works off the DOM alone costs no
  // request. Missing entries mean unknown, not zero.
  workFacts: () => Promise<Map<number, WorkFacts>>;
  // Called exactly once, at the moment the arm's treatment actually lands on
  // the page — never on a control render — and defaulting to 'rendered'. It
  // fires the one render event this wave measures exposure by, and it is what
  // every later ctx.track carries as render_state, outranking a stamp-hook
  // prediction. First call wins; repeats are no-ops.
  markRendered: (state?: ArmRenderState) => void;
  // source is clamped to the 64 chars the warehouse column holds. entityId is
  // numeric because the clicks controller runs Integer(..., exception: false):
  // a slug would land NULL and a zero-padded id would parse as octal.
  track: (
    source: string,
    options?: { entityId?: number | null; extra?: object },
  ) => void;
};

// The aux stamp keys an arm may contribute; autotest/autotest_bucket/mode stay
// harness-owned. Values are pinned unions — analytics GROUP BYs on these keys
// must never meet an ad-hoc dialect or a typo.
export type ArmRenderState = 'rendered' | 'fallback' | 'partial';

export type ArmStamp = {
  render_state?: ArmRenderState;
};

export type ArmSpec = {
  // Resolved once, before anything renders, so render_state rides every stamped
  // click; must be side-effect free (reads only). It is a synchronous
  // prediction from pre-render inputs, so derive it from the same predicate
  // that gates the arm, never by probing rendered DOM. An arm whose outcome
  // depends on the resolved workFacts() cannot report it truthfully here.
  //
  // Synchronous where the two hooks below are not, and deliberately: onReady
  // may track a click on its first line, so an awaited stamp would ship that
  // click — and every one until the fetch landed — with no render_state column
  // at all. A prediction that has to be awaited is not a prediction.
  stamp?: (ctx: ArmContext) => ArmStamp | undefined;
  onReady?: (ctx: ArmContext) => void | Promise<void>;
  // Runs against the server-rendered cards, then against each batch the
  // Backbone pagination appends. Must be idempotent per card.
  onCards?: (cards: ProfileCard[], ctx: ArmContext) => void | Promise<void>;
};

// Wave-key buckets dispatch through these specs; smoke-key buckets never reach
// this table (harness gate) and keep rendering control. An arm whose
// predicates fail yields no mutation and renders control pixel-for-pixel.
export const ARMS: Partial<Record<Arm, ArmSpec>> = {};
